import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { SecondaryButton } from "src/ui/shared/CustomButton";
import { copyToClipboard } from "src/ui/utils/clipboard";
import { useStreamingLog } from "src/ui/utils/useStream";

import { Product } from "@dashboard/products/types";
import { LogStreamModal, StreamStatus } from "@shared/LogStreamModal";

import { buildLogLink, getReasonMessage, onDownloadLog, onStartStreaming } from "./LogStreamer.utils";

interface Props {
    buildId: number;
    hasLogs: boolean;
    disabled?: boolean;
    product?: Product;
    workspaceGuid?: string;
}

export const LogStreamer = (props: Props): React.JSX.Element => {
    const [logStreamerOpen, setLogStreamerOpen] = React.useState(false);
    const [downloading, setDownloading] = React.useState(false);
    const [status, setStatus] = React.useState<StreamStatus>("initializing");

    const [params] = useSearchParams();
    const logParam = params.get("log");

    const { t } = useTranslation(["modals", "common"]);

    const { product, workspaceGuid } = props;
    const onCopyLink =
        product && workspaceGuid
            ? () =>
                  void copyToClipboard(
                      buildLogLink({
                          buildId: props.buildId,
                          codename: product.codename,
                          androidVersion: product.androidVersion,
                          workspaceGuid,
                      }),
                  )
            : undefined;

    const { containerRef, scrollContainerRef, onClearStream, onRunStream } = useStreamingLog({
        onStartStreaming: () => onStartStreaming(props.buildId),
        onStatusChange: setStatus,
    });

    const downloadLog = async () => {
        setDownloading(true);
        await onDownloadLog(props.buildId);
        setDownloading(false);
    };

    const onOpen = () => {
        setLogStreamerOpen(true);
        onRunStream();
    };

    const onClose = () => {
        setLogStreamerOpen(false);
        onClearStream();
    };

    React.useLayoutEffect(() => {
        if (logParam === String(props.buildId)) {
            onOpen();
        }
    }, [props.buildId, logParam]);

    return (
        <React.Fragment>
            <SecondaryButton
                onClick={onOpen}
                disabled={!props.hasLogs || props.disabled}
                tooltipProps={{ title: getReasonMessage(props.hasLogs) }}
            >
                <i className="fa-solid fa-align-left" />
            </SecondaryButton>

            <LogStreamModal
                status={status}
                downloadDisabled={!props.hasLogs}
                open={logStreamerOpen}
                onClose={onClose}
                title={t("modals:logStreamer.buildLogsTitle", { id: props.buildId })}
                onDownload={downloadLog}
                onCopyLink={onCopyLink}
                downloading={downloading}
                containerRef={scrollContainerRef}
                contentRef={containerRef}
            />
        </React.Fragment>
    );
};
